import { useState, useEffect } from 'react'
import HeaderBD from './components/HeaderBD';
import FormTracker from './components/FormTracker';
import APPSETTINGS from './appsettings.json';
import './App.css';
import bdLogoWhiteFooter from './img/bd-logo-white-footer.png';
import { BasicInfo, Facility, init_BasicInfo, init_Facility, init_Institution, Institution, LanguageNames, Application, RegistrationValue, PositionValue } from './interfaces/Interfaces';
import { API, Endpoints } from './api/API';
import { Redirect, Route, Switch } from 'react-router-dom';
import ProfileEdit from './components/ProfileEdit';
import { AddApplication } from './components/AddApplication';
import { getJWT } from './js/jwt';
import { replaceAll } from './js/verificationFunctions';
import Contact from './components/Contact';
import { useAppData, userDataCtx } from './hooks/applicationData';

const languageValues: LanguageNames = {
  "en-US": 'English (United States)',
  "fr-CA": "Français (Canada)"
}

function App() {
  const {data, setData, initData, validationFlags, setValidationFlags} = useAppData();
  // State to keep track of form step, starts at 2 because step 1 is in b2c (keeps code in line with visuals)
  const [formStep, setFormStep] = useState<number>(2);
  // States to get data from URL
  const [appName, setAppName] = useState<string>('BD');
  const [token, setToken] = useState<string>('');
  const [userType, setUserType] = useState<"customer" | "independent" | "student" | null>("customer");
  const [lang, setLang] = useState<string | null>("en-US");
  const [userCountry, setUserCountry] = useState<string | null>("");
  const [objectId, setObjectId] = useState<string | null>("");
  const [appId, setAppId] = useState<string | null>("");
  const [langObj, setLangObj] = useState<any>();
  const [backgroundWIP, setBackgroundWIP] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');
  const CONFIG = require('./config.json')[process.env.NODE_ENV];
  const [email, setEmail] = useState('');
  const [basicInfo, setBasicInfo] = useState<BasicInfo>(init_BasicInfo);
  const [facZip, setFacZip] = useState<string>("");
  const [facilityInfo, setFacilityInfo] = useState<Array<Facility>>([{ ...init_Facility }]);
  const [facilityOptional, setFacilityOptional] = useState<boolean>(false);
  const [hideLanguageSelector, setHideLanguageSelector] = useState<boolean>(false);
  const [skipOptionalFacility, setSkipOptionalFacility] = useState<boolean>(false);
  const [appFeatures, setAppFeatures] = useState<any>(null);
  const [hideFields, setHideFields] =  useState<Array<string>>([]);
  const [optionalFields, setOptionalFields] = useState<Array<string>>([]);
  const [skipBDLAStep, setskipBDLAStep] = useState<boolean>(false);

  const [institutionLinkAlwaysOptional, setInstitutionLinkAlwaysOptional] = useState<boolean>(false);
  const [skipOptionalInstitutionLink, setSkipOptionalInstitutionLink] = useState<boolean>(false);

  // Instituiton Information states
  const [institute, setInstitute] = useState<Institution>({ ...init_Institution });
  // Other Basic Information states
  const [bdBioSciencesUserType, setBdBioSciencesUserType] = useState<string | null>("");
  // This state manages the positions retrived from getPositionsApi
  const [positions, setPositions] = useState<Array<[string, string]>>([]);
  const [userExisted, setUserExisted] = useState<"progressiveProfiling" | "profileEdit" | "addApplication">();
  const [timezone, setTimezone] = useState('');
  const [showContact, setShowContact] = useState(false);
  const [reason, setReason] = useState<string>('');
  const [countries, setCountries] = useState<Record<string, string>>();
  const countryURL = process.env.NODE_ENV === 'production' ? APPSETTINGS.production.dataTranslation : APPSETTINGS.development.dataTranslation;
  const [contactName, setContactName] = useState<string>("");
  const [contactEmail, setContactEmail] = useState<string>("");
  const [contactOldEmail, setContactOldEmail] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [applications, setApplications] = useState<Array<Application>>([]);
  const [titleGuid, setTitleGuid] = useState<string>('');
  const [globalDataObj, setGlobalDataObj] = useState({});
  const [authLocale, setAuthLocale] = useState<string>('');
  const [accountID, setAccountID] = useState<string>('');

  const [customRegistrationValues, setCustomRegistrationValues] = useState<Array<RegistrationValue>>([]);
  //const [positionAPIData, setPositionAPIData] = useState<Record<string, Array<string>>>();
  const [positionAPIData, setPositionAPIData] = useState<PositionValue>();
  const branch_version = process.env.NODE_ENV === 'production' ? APPSETTINGS.production.branchVersion : APPSETTINGS.development.branchVersion;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    var clientId = urlParams.get("clientIdClaim");
    if (clientId != null) {
      clientId = decodeURIComponent(clientId.replace(/\+/g, " "));
      clientId = clientId.replace("/", "");
    }

    var strAuthLocale = urlParams.get("authLocale");
    console.log("URL authLocale is: " + authLocale);
    setAuthLocale(strAuthLocale ? strAuthLocale : '');

    var strAccountID = urlParams.get("accountID");
    console.log("AccountID is: " + strAccountID);
    setAccountID(strAccountID ? strAccountID : '');

    const urlLanguageClaim = urlParams.get("languageClaim")
    if (urlLanguageClaim !== null)
      setLang(urlLanguageClaim);
    let country = urlParams.get("country")!;
    setUserCountry(country);
    let oid = urlParams.get("oid")!;
    setObjectId(oid);
    setAppId(clientId);
    if (urlParams.get("progProfToken") === null) {
        window.location.replace('https://mybdqa.azurewebsites.net/');
    }
    setToken(urlParams.get("progProfToken")!);

    if (token) {
      API.POST({ "id": clientId }, Endpoints.GetApplicationData, token)
        .then(res => {          
          setAppName(res.name);
          setFacilityOptional(res.facilityLinkAlwaysOptional ?? false);
          setSkipOptionalFacility(res.skipOptionalFacilityLink ?? false);
          setInstitutionLinkAlwaysOptional(res.customMetadata.institutionLinkAlwaysOptional ?? false);
          setSkipOptionalInstitutionLink(res.customMetadata.skipOptionalInstitutionLink ?? false);
          setAppFeatures(res.features ?? null);
          setHideLanguageSelector(res.features?.hideLanguageSelector ? res.features.hideLanguageSelector : false);
          getUserData(oid, country, res.facilityLinkAlwaysOptional ?? false, res.skipOptionalFacilityLink ?? false);
          setHideFields(res.features ? res.features.hideFields : []);
          setOptionalFields(res.features ? res.features.optionalFields : []);
        });

      API.POST({ "id": clientId, "locale": authLocale }, Endpoints.GetRegistrationValuesForApplication, token)
        .then(res => {
          //debugger
          setCustomRegistrationValues(res);
        });

      API.POST({}, Endpoints.GetApplicationsList, token)
        .then(res => {
          let apps: Array<Application> = [];
          if (res.applications) {
            res.applications.forEach((element: Application) => {
              if (element.isActive !== undefined && element.isActive === false) return;
              apps.push(element);
            });
          }
          setApplications(apps);
        });
    }
  }, [token]);

  useEffect(() => {
    if (token !== '') {      
      const userLang = authLocale? authLocale.split('-')[0] : 'en';
      console.log("userLang: "+ userLang);
      API.POST({"lang" : userLang }, Endpoints.GetPositions, token)
        .then((dataList) => {          
          setPositionAPIData(dataList);
        });
    }
  }, [token]);

  useEffect(() => {
    if (titleGuid !== '') {
      if (langObj === undefined || langObj === null) {
        const translationURL = process.env.NODE_ENV === 'production' ? APPSETTINGS.production.translationURL : APPSETTINGS.development.translationURL;
        fetch(translationURL)
          .then(response => response.json())
          .then(data => setLangObj(data));
      }
      else {
        translateElements();
        translateCountries();
        getPositions();
      }
    }
  }, [lang, titleGuid]);

  useEffect(() => {
    if (langObj !== undefined) {
      translateElements();
      translateCountries();
      getPositions();
    }
  }, [langObj])

  // Effect to get user's data and autopopulate fields
  // useEffect(() => {
  //   if (objectId !== "")
  //     getUserData();
  // }, [objectId]);

  useEffect(() => {
    getPositions();
  }, [positionAPIData])

  useEffect(() => {
    //debugger
    updateRegistrationValues();
  }, [customRegistrationValues, authLocale])

  const translateCountries = () => {
    var langLower = lang ? lang.toLowerCase() : 'en-us';
    if (langLower == 'en-us' || langLower == 'fr-ca') {
      fetch(countryURL + `countries-${langLower}.json`)
        .then((res) => res.json())
        .then((data) => {
          setCountries(data);
        });
    }
  }

  const getPositions = () => {
    if (positionAPIData !== undefined) {
      var displayPostions: Array<[string, string]>=[];
      if (positionAPIData.titles !== undefined) {
        displayPostions = positionAPIData.titles.flatMap((pos: any) => Object.entries(pos));
      }
      setPositions(displayPostions);
      
    }
  }

  const updateRegistrationValues = () => {
    if (customRegistrationValues !== undefined) {
      const supportedDropDownIds = ["position", "country", "LA_Role_Select", "LA_care_setting_select", "MB_role", "ddlSoC", "MB_Ticket_Role_Select", "MB_training_role_select"];

      //set custom fields
      for (let element of customRegistrationValues) {
        if (element.displayText != null && element.displayText.length > 0) {
          updateCustomDisplay(element.fieldName, element.displayText);
        }

        if (element.values != null && element.values.length > 0 && supportedDropDownIds.includes(element.fieldName)) {
          updateCustomDropDown(element.fieldName, element.values);
        }
      }
    }
  }

  const updateCustomDisplay = (fieldName: string, displayText: string) => {
    if (displayText != null) {
      //Delay slightly to avoid conflict with existing translate feature
      setTimeout(() => {
        $(`.${fieldName}`).text(displayText);
      }, 250);
    }
  }

  const updateCustomDropDown = (fieldName: string, values: Array<{ name: string, value: string }>) => {
    // let el = $(`#${fieldName}`);
    // if (values != null && values.length > 0) {
    //   el.empty();
    //   el.append($('<option></option>').val('').html('Select...'));
    //   for (const option of values) {
    //     el.append($('<option></option>').val(option.value).html(option.name));
    //   }
    // }
  }

  const getJWTDriver = async (jwtResetPassword: boolean = false): Promise<boolean> => {
    var result = await getJWT({ email: email, basicInfo: basicInfo, userType: userType!, jwtResetPassword: jwtResetPassword }, token);
    if (!result) {
      setBackgroundWIP(false);
      return false;
    }
    return true;
  }


  /*
  Any component that mounts without causing the entire app to rerender will need to have the translate function passed as a prop and use this useEffect hook
  useEffect(() => {
        props.translate();
    }, []);
  */
  function translateElements() {
    // Messy solution to get concept working
    for (const guid in langObj) {

      //If going to allow for multiple elements
      if (lang) {
        $(`.${guid}:not('input'):not('option')`).html(langObj[guid][lang]);
        $(`option.${guid}`).text(langObj[guid][lang]);
        $(`input.${guid}`).attr("placeholder", langObj[guid][lang]);
      }

      $('.copyright-year').text(new Date().getFullYear());
    }

    if (titleGuid !== '' && lang && langObj !== undefined) {
      document.title = langObj[titleGuid][lang];
    }

    // Fix any height issues
    $('.match-height').matchHeight();
  }

  useEffect(() => {
    translateElements();
  }, [data])

  const notNullOrUndef = (toCheck: any): boolean => {
    return (toCheck !== null && toCheck !== undefined)
  }

  const facilitySelectionRequired = (facilityArray: Array<Facility> | undefined, userType: string | null, facilityLinkAlwaysOptional: boolean, skipOptionalFacilityLink: boolean): boolean => {
    if(facilityArray && facilityArray.length > 0 && JSON.stringify(facilityArray[0]) !== JSON.stringify(init_Facility)){
      return false;
    }
    else if (userType === 'independent') {
      // If the facility link is always optional and the user has chosen to skip the optional facility link, return false
      return !skipOptionalFacilityLink;
    }
    else if(userType !== 'customer' || facilityLinkAlwaysOptional){
      return false;
    }

    return true;
  }

  const institutionSelectionRequired = (userType: string | null, institutionLinkAlwaysOptional: boolean): boolean => {
    if(userType !== 'student' || institutionLinkAlwaysOptional){
      return false;
    }

    return true;
  }

  // API call to fetch user's data
  const getUserData = async (oid: string, country: string, facilityLinkAlwaysOptional: boolean, skipOptionalFacilityLink: boolean) => {  
    const body = {
      objectId: oid,
      country: country,
      applicationId: appId,
      returnNested: true
    };

    try {
      setBackgroundWIP(true);
      const d = await API.POST(body, Endpoints.GetUserData, token);

      setEmail(d.email);
      setUserType(d.userType);

      if (notNullOrUndef(d.defaultLanguage) && d.defaultLanguage !== '') {
        setLang(d.defaultLanguage);
      }

      //Check user's global object
      //If LA_role, LA_care_setting and LA_RN_Number values are present, skip BDLA step
      if(appId == CONFIG.learningAcademyLevel2 || appId== CONFIG.learningAcademyLevel3)
      {
          if(d["global"] != null)
          {
            if(d["global"]["LA_role"] != null && d["global"]["LA_care_setting"] != null)
            {
                setskipBDLAStep(true);
            }
          }
      }

      if (notNullOrUndef(d.firstName) && notNullOrUndef(d.lastName)) {
        autoFillFields(d);

        if (d['applications'][appId!] !== undefined && d['applications'][appId!]['userProvisionStatus'] !== undefined && d['applications'][appId!].userProvisionStatus !== 'Preloaded') {
          $('#flow-indicator').removeClass('5c065f06-c0cd-4ddd-8413-ef190b6d09b5');
          $('#flow-indicator').text('Edit Profile');
          $('#flow-indicator').addClass('05e278c6-6192-4636-b37e-2ea05aa46a0f');
          setUserExisted("profileEdit");
        }
        else {          
          if ( 
              //@ts-ignore
            Object.values(CONFIG).some((value:string) => appId === value) 
            || facilitySelectionRequired([...d["global"]["facilities"]], d.userType, facilityLinkAlwaysOptional, skipOptionalFacilityLink)
            || (d['applications'][appId!] !== undefined && d['applications'][appId!].userProvisionStatus === 'Preloaded')
          ){
            //remove id_token if present
            let sPos = window.location.href.indexOf('#/id_token');
            if (sPos > -1) {
              window.location.href = window.location.href.substring(0, sPos);
            }

            setGlobalDataObj({...d["global"]});
            setUserExisted("addApplication");
          }
          else {
            var quickSaveBody = { id: d.id, country: d.country, applications: { [appId!]: {} } };
            await API.POST(quickSaveBody, Endpoints.SaveUserData, token);
            if (!getJWT({
              email: d.email,
              basicInfo: {
                firstName: d.firstName,
                lastName: d.lastName,
                phoneNumber: d.phoneNumber,
                position: d["global"]["position"],
                mobileNumber: d["global"]["mobileNumber"],
                faxNumber: d["global"]["faxNumber"],
                accountNumber: d["global"]["accountNumber"],
                department: d["global"]["department"],
                isActive: d.isActive,
                authLocale: d["global"]["authLocale"]
              },
              userType: d.userType!,
              jwtResetPassword: false
            }, token))
              setBackgroundWIP(false);
            return;
          }
        }


        setDisplayName(d.firstName + ' ' + d.lastName);
      }
      else {
        setUserExisted('progressiveProfiling');
        setBackgroundWIP(false);
      }
    } catch (e) {
      console.log(e);
    }

    setBackgroundWIP(false);
  }

  // Function to let First time sign up code look cleaner (set a single facility instead of always setting an array with one facility)
  const setSignUpFacility = (fac: Facility) => {
    setFacilityInfo([{ ...fac }]);
  }

  const autoFillFields = (responseData: any) => {    
    const basicInfoBody: BasicInfo = {
      firstName: responseData.firstName,
      lastName: responseData.lastName,
      phoneNumber: responseData.phoneNumber,
      position: responseData["global"]["position"],
      mobileNumber: responseData["global"]["mobileNumber"],
      faxNumber: responseData["global"]["faxNumber"],
      accountNumber: responseData["global"]["accountNumber"],
      department: responseData["global"]["department"],
      isActive: responseData.isActive,
      authLocale: authLocale
    };

    setContactName(responseData.firstName + ' ' + responseData.lastName);
    setContactEmail(responseData.email);
    setContactOldEmail("");

    setTimezone(responseData.timezone);

    if (responseData["global"]["zipCode"] !== undefined) {
      setFacZip(responseData["global"]["zipCode"]);
    }

    if (responseData["global"]["facilities"] !== undefined && responseData["global"]["facilities"].length > 0 && JSON.stringify(responseData["global"]["facilities"][0]) !== JSON.stringify(init_Facility)) {
      setFacilityInfo([...responseData["global"]["facilities"]]);
    }

    if (responseData["global"]["institution"] !== undefined && JSON.stringify(responseData["global"]["institution"]) !== JSON.stringify(init_Institution)) {
      setInstitute({ ...responseData["global"]["institution"] });
    }
  
    setBasicInfo(basicInfoBody);
    setUserType(responseData.userType);
    
    if (responseData["applications"][appId!]){
        initData({...responseData["applications"][appId!]});
    }
  }


  //translateElements();

  return (
  <userDataCtx.Provider value={{data, setData, validationFlags, setValidationFlags}}>
  <div className={backgroundWIP ? "App background-wip loading" : "App"}>
      <HeaderBD lang={lang} setLang={setLang} displayName={displayName} userExisted={userExisted} countryURL={countryURL} appId={appId} hideLanguageSelector={hideLanguageSelector} />
      <div hidden>Branch Version: {branch_version}</div>
      <nav className="sub">
        <div id="register-welcome" className="label">
          {appName} {userExisted === 'profileEdit' ? <span id="flow-indicator" className="05e278c6-6192-4636-b37e-2ea05aa46a0f">My Profile - {displayName}</span> : <span id="flow-indicator" className="5c065f06-c0cd-4ddd-8413-ef190b6d09b5">Registration</span>}
        </div>
      </nav>
      <main className="off-white">
        <div className="main-container">
          <Switch>
            <Route exact path='/' >
              {
                (userExisted === undefined || userExisted === null) ?
                  <div className="loading-container">
                    <h2 className='loading'>Loading...</h2>
                  </div>
                  :
                  userExisted === 'profileEdit' ?
                    <Redirect to="/profile-edit" />
                    :
                    userExisted === 'addApplication' ?
                      <Redirect to={`/new-application/${replaceAll(appName, ' ', '')}`} />
                      :
                      <Redirect to="/progressive-profiling" />
              }
            </Route>
            <Route path='/progressive-profiling'>
              {
                userExisted === 'progressiveProfiling' ?
                  <FormTracker
                    formStep={formStep}
                    setFormStep={setFormStep}
                    userType={userType}
                    lang={lang}
                    userCountry={userCountry}
                    setUserCountry={setUserCountry}
                    objectId={objectId}
                    appId={appId}
                    translate={translateElements}
                    setBackgroundWIP={setBackgroundWIP}
                    email={email}
                    basicInfo={basicInfo}
                    setBasicInfo={setBasicInfo}
                    bdBioSciencesUserType={bdBioSciencesUserType}
                    setBdBioSciencesUserType={setBdBioSciencesUserType}
                    facZip={facZip}
                    setFacZip={setFacZip}
                    positions={positions}
                    facilityInfo={facilityInfo[0]}
                    setFacilityInfo={setSignUpFacility}
                    institute={institute}
                    setInstitute={setInstitute}
                    getJWTDriver={getJWTDriver}
                    CONFIG={CONFIG}
                    countries={countries}
                    token={token}
                    setTitleGuid={setTitleGuid}
                    facilityOptional={facilityOptional}
                    skipOptionalFacility={skipOptionalFacility}
                    appFeatures={appFeatures}
                    institutionLinkAlwaysOptional={institutionLinkAlwaysOptional}
                    skipOptionalInstitutionLink={skipOptionalInstitutionLink}
                    authLocale={authLocale}
                    accountID={accountID}
                    hideFields={hideFields}
                    optionalFields={optionalFields}
                    customRegistrationValues={customRegistrationValues}
                    updateRegistrationValues={updateRegistrationValues}
                    skipBDLAStep={skipBDLAStep}
                  /> :
                  <Redirect to="/" />}
            </Route>
            <Route path='/profile-edit'>
              {
                userExisted === 'profileEdit' ?
                  <ProfileEdit
                    lang={lang}
                    setLang={setLang}
                    languageValues={languageValues}
                    userCountry={userCountry}
                    setUserCountry={setUserCountry}
                    objectId={objectId}
                    appId={appId}
                    translate={translateElements}
                    setBackgroundWIP={setBackgroundWIP}
                    setDisplayName={setDisplayName}
                    email={email}
                    basicInfo={basicInfo}
                    setBasicInfo={setBasicInfo}
                    bdBioSciencesUserType={bdBioSciencesUserType}
                    setBdBioSciencesUserType={setBdBioSciencesUserType}
                    positions={positions}
                    getJWTDriver={getJWTDriver}
                    facilityInfo={facilityInfo}
                    facZip={facZip}
                    setFacZip={setFacZip}
                    institute={institute}
                    userType={userType}
                    timezone={timezone}
                    setTimezone={setTimezone}
                    setShowContact={setShowContact}
                    setReason={setReason}
                    countries={countries}
                    token={token}
                    setContactEmail={setContactEmail}
                    setContactName={setContactName}
                    setMessage={setMessage}
                    setTitleGuid={setTitleGuid}
                    facilityOptional={facilityOptional}
                    skipOptionalFacility={skipOptionalFacility}
                    institutionLinkAlwaysOptional={institutionLinkAlwaysOptional}
                    skipOptionalInstitutionLink={skipOptionalInstitutionLink}
                    hideFields={hideFields}
                    optionalFields={optionalFields}
                  />
                  : <Redirect to="/" />}
            </Route>
            <Route path='/new-application/:newApplication'>
              <AddApplication
                appName={appName}
                lang={lang}
                userCountry={userCountry}
                setUserCountry={setUserCountry}
                objectId={objectId}
                appId={appId}
                translate={translateElements}
                setBackgroundWIP={setBackgroundWIP}
                setDisplayName={setDisplayName}
                email={email}
                accountNum={accountID !="" ? accountID: basicInfo.accountNumber}
                bdBioSciencesUserType={bdBioSciencesUserType}
                setBdBioSciencesUserType={setBdBioSciencesUserType}
                countries={countries}
                getJWTDriver={getJWTDriver}
                token={token}
                global={globalDataObj}
                needFacility={facilitySelectionRequired(facilityInfo, userType, facilityOptional, skipOptionalFacility)}
                needInstitution={institutionSelectionRequired(userType, institutionLinkAlwaysOptional)}
                appFeatures={appFeatures}
                facZip={facZip}
                setTitleGuid={setTitleGuid}
                customRegistrationValues={customRegistrationValues}
                updateRegistrationValues={updateRegistrationValues}
                authLocale={authLocale}
              />
            </Route>
          </Switch>
        </div>
        <div id="contact-us" className="align-center">
          <div className="spacer"></div>
          <span className="a6fdd8ef-fc2f-40b5-9624-4226d540041e">If you are having issues logging in, please</span><span> </span>
          <a id='contact-toggle' href="#" className="b1eee5d1-90cb-465f-b231-7614c9dd6b74" onClick={() => setShowContact(true)}> contact us</a>
        </div>
        {showContact ?
          <Contact
            appId={appId}
            objectId={objectId}
            userCountry={userCountry}
            reason={reason}
            setReason={setReason}
            translate={translateElements}
            setShowContact={setShowContact}
            contactName={contactName}
            setContactName={setContactName}
            contactEmail={contactEmail}
            setContactEmail={setContactEmail}
            message={message}
            setMessage={setMessage}
            token={token}
            applications={applications}
            contactOldEmail={contactOldEmail}
            setContactOldEmail={setContactOldEmail} /> :
          <></>
        }
      </main >
      <footer>
        <div className="logo-disclamer">
          <a className="logo" href="index.html"><img src={bdLogoWhiteFooter}
            alt="BD Griffin" /></a>
          <div className="disclaimer">
            <p className="copyright b15bceb5-4717-4f30-9960-b38b00193864">© 2021 BD. All rights reserved. Unless otherwise noted BD, the BD Logo and all other trademarks are property of Becton, Dickinson and Company</p>
          </div>
        </div>

        <div className="support-links">
          <a id='privacy-policy-link' className='footerPrivacyTranslation' href='http://www.bd.com/en-us/privacy-policy' target='_blank' rel='noopener noreferrer'>Privacy</a>
          <span className="pipe">|</span>
          <a id='terms-of-use-link' className='footerTermsTranslation' href='http://www.bd.com/en-us/terms-of-use' target='_blank' rel='noopener noreferrer'>Terms &amp; Conditions</a>
          <span className="pipe">|</span>
          <a id='cookies-link' className='footerCookiesTranslation' href='http://www.bd.com/en-us/cookies' target='_blank' rel='noopener noreferrer'>Cookies</a>
          <span className="pipe">|</span>
          <a id='accessibility-link' className='footerAccessibilityTranslation' href='http://www.bd.com/en-us/accessibility' target='_blank' rel='noopener noreferrer'>Accessibility</a>
        </div>
      </footer>
    </div >
    </userDataCtx.Provider>
  );
}

export default App;
